// ---------------------------------------------------------------
// DOT Driver Qualification (DQ) file model — 49 CFR Part 391 + drug/alcohol (Part 382).
// This is a compliance *management* tool, not a certified ELD.
// ---------------------------------------------------------------

// Each item: key, label, regulation, kind (check | date | dateResult), and whether it renews annually.
export const DQ_ITEMS = [
  { key: 'application', label: "Driver's employment application", reg: '391.21', kind: 'check', required: true },
  { key: 'mvrHire', label: 'MVR obtained at hire', reg: '391.23(a)(1)', kind: 'date', required: true },
  { key: 'psp', label: 'Safety performance history (PSP / prior employers)', reg: '391.23', kind: 'date', required: true },
  { key: 'roadTest', label: 'Road test certificate (or CDL in lieu)', reg: '391.31 / 391.33', kind: 'date', required: true },
  { key: 'medical', label: "Medical examiner's certificate", reg: '391.43', kind: 'date', required: true, renews: true },
  { key: 'medRegistry', label: 'National Registry examiner verification', reg: '391.51(b)(9)', kind: 'check', required: true },
  { key: 'mvrAnnual', label: 'Annual review of driving record', reg: '391.25', kind: 'date', required: true, renews: true },
  { key: 'violationCert', label: 'Annual driver certificate of violations', reg: '391.27', kind: 'date', required: true, renews: true },
  { key: 'drugPre', label: 'Pre-employment drug test', reg: '382.301', kind: 'dateResult', required: true },
  { key: 'clearinghousePre', label: 'Clearinghouse pre-employment query', reg: '382.701(a)', kind: 'date', required: true },
  { key: 'clearinghouseAnnual', label: 'Clearinghouse annual query', reg: '382.701(b)', kind: 'date', required: true, renews: true },
  { key: 'eltTraining', label: 'Entry-level driver training (if applicable)', reg: '380 Subpart F', kind: 'check', required: false },
];

const DAYS = 864e5;

// status of one item: 'good' | 'expiring' | 'expired' | 'missing' | 'na'
export function itemStatus(item, data = {}, medicalExpiry) {
  const d = data[item.key] || {};
  // check-type items
  if (item.kind === 'check') {
    if (d.done) return 'good';
    return item.required ? 'missing' : 'na';
  }
  // medical uses the driver's medicalExpiry field if present
  if (item.key === 'medical') {
    const exp = d.expiry || medicalExpiry;
    if (!d.date && !exp) return 'missing';
    if (exp) {
      const days = (new Date(exp) - Date.now()) / DAYS;
      if (days < 0) return 'expired';
      if (days <= 30) return 'expiring';
    }
    return 'good';
  }
  if (!d.date) return item.required ? 'missing' : 'na';
  if (item.renews) {
    const age = (Date.now() - new Date(d.date)) / DAYS;
    if (age > 365) return 'expired';
    if (age > 335) return 'expiring';
  }
  if (item.kind === 'dateResult' && d.result && /fail|positive|refus/i.test(d.result)) return 'expired';
  return 'good';
}

export function readinessScore(data, medicalExpiry) {
  const required = DQ_ITEMS.filter((i) => i.required);
  const good = required.filter((i) => itemStatus(i, data, medicalExpiry) === 'good').length;
  return Math.round((good / required.length) * 100);
}

export function driverSafetyFlags(driver) {
  const A = (driver && driver.attributes) || {};
  const data = A.safety || {};
  const out = [];
  DQ_ITEMS.forEach((item) => {
    const s = itemStatus(item, data, A.medicalExpiry);
    if (s === 'expired' || s === 'missing' || s === 'expiring') out.push({ item, status: s });
  });
  return out;
}

export const STATUS_STYLE = {
  good: { bg: '#f0fdf4', color: '#15803d', dot: '#22c55e', text: 'Complete' },
  expiring: { bg: '#fffbeb', color: '#b45309', dot: '#f59e0b', text: 'Expiring' },
  expired: { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', text: 'Expired / Fail' },
  missing: { bg: '#f1f5f9', color: '#64748b', dot: '#cbd5e1', text: 'Missing' },
  na: { bg: '#f8fafc', color: '#94a3b8', dot: '#e2e8f0', text: 'N/A' },
};
